import {
  Account,
  BankFragment,
  Currency,
  Mandate
} from 'graph/generated/partner/graphql-types'

export interface IBankAccount
  extends Pick<BankFragment, 'externalId' | 'logo'>,
    Account {
  customName: string
  bankName: string
  disabled?: boolean
  mandates?: Mandate[]
}

export interface BankAccounts {
  accounts: IBankAccount[]
  banks: BankFragment[]
}

/**
 * Get user-friendly error message by internal error code
 */
export function getBankVerificationErrorMessage(errorCode: string): string {
  const errorMessages: Record<string, string> = {
    '1001':
      "The bank statement doesn't meet our requirements (missing info, low quality, or unsupported format). Upload a clearer, recent statement that shows the full account details.",
    '1002':
      "The bank account couldn't be verified. Please review your account details and try again.",
    '1003':
      'Some bank account details are missing or invalid. Please review and update the highlighted fields.',
    '1004':
      'We need an official bank statement for this account. Upload one issued by your bank within the last 12 months.',
    '1005':
      "The account details don't match the uploaded statement. Review the account numbers, update any incorrect fields or upload a new statement that shows the correct information."
  }

  return (
    errorMessages[errorCode] ||
    'An error occurred during bank account verification.'
  )
}

export function getBankVerificationRemediationButtonText(
  errorCode: string
): string {
  switch (errorCode) {
    case '1001':
    case '1004':
      return 'Upload bank statement'
    default:
      return 'Review items'
  }
}

/**
 * Get the fields that should be highlighted based on error code
 * Returns an array of field names that need warning300 highlighting
 */
export function getBankVerificationHighlightedFields(
  errorCode: string
): ('bankUpload' | 'bankAccountDetails')[] {
  // Bank statement related errors - highlight bank upload
  const bankStatementErrors = ['1001', '1004']

  // Bank account details related errors - highlight bank account details
  const bankAccountDetailsErrors = ['1002', '1003', '1005']

  const fieldsToHighlight: ('bankUpload' | 'bankAccountDetails')[] = []

  if (bankStatementErrors.includes(errorCode)) {
    fieldsToHighlight.push('bankUpload')
  }

  if (bankAccountDetailsErrors.includes(errorCode)) {
    fieldsToHighlight.push('bankAccountDetails')
  }

  return fieldsToHighlight
}

export const setBankAccounts = (banks: BankFragment[]): BankAccounts => {
  const setAccounts = () =>
    banks.reduce((prev: IBankAccount[], curr) => {
      const { accounts, externalId, name, logo } = curr
      const acc = accounts.map(
        i =>
          ({
            ...i,
            bankName: name,
            logo,
            externalId
          }) as IBankAccount
      )
      return [...prev, ...acc]
    }, [])
  return {
    accounts: setAccounts(),
    banks
  }
}

export const bankAccountHasPADAuthorization = (bankAccount: {
  mandates?: {
    id: string
    consentAt: string
    version: string
  }[]
}) => bankAccount?.mandates?.length > 0

/**
 * Format account subtype with proper label based on currency
 * Canadian accounts use "Chequing" while US accounts use "Checking"
 *
 * @param subtype - The account subtype (e.g., "CHECKING", "SAVINGS", "checking", "savings")
 * @param isCanadian - Whether the account is Canadian (CAD currency)
 * @returns Formatted subtype label
 */
export const formatAccountSubtype = (
  subtype?: string | null,
  isCanadian = false
): string => {
  if (!subtype) return ''

  const normalizedSubtype = subtype.toLowerCase()

  if (normalizedSubtype === 'checking') {
    return isCanadian ? 'Chequing' : 'Checking'
  }

  if (normalizedSubtype === 'savings') {
    return 'Savings'
  }

  // Fallback to capitalize first letter for other types
  return subtype.charAt(0).toUpperCase() + subtype.slice(1).toLowerCase()
}

/**
 * Returns the appropriate bank transfer label based on currency
 * Canadian accounts use EFT, US accounts use ACH
 */
export const getBankTransferLabel = (currency?: Currency): string =>
  currency === Currency.Cad ? 'EFT' : 'ACH'
